// Release asset storage — Fire TV / Google TV player APKs.
//
// Files live in the public `releases` Supabase Storage bucket at well-known
// per-platform paths so the URL is stable across uploads:
//
//   firetv/signage-fire-tv-latest.apk     — Fire TV APK binary
//   firetv/manifest.json                  — Fire TV version / sha256 / filename
//   googletv/signage-google-tv-latest.apk — Google TV APK binary
//   googletv/manifest.json                — Google TV version / sha256 / filename
//
// The bucket is public-read so the download URL works without auth. Uploads
// require an authenticated Supabase session (any admin signed in to the app).

import { supabase } from '@/lib/supabase';
import type { FireTvApkDownload } from './fireTvApk';
import { buildFireTvSideloadGuideUrl } from './fireTvApk';
import type { GoogleTvApkDownload } from './googleTvApk';
import { buildGoogleTvSideloadGuideUrl } from './googleTvApk';
import { resolveGithubRepoSlug } from './githubRelease';

const BUCKET = 'releases';

const FIRETV_APK_PATH = 'firetv/signage-fire-tv-latest.apk';
const FIRETV_MANIFEST_PATH = 'firetv/manifest.json';

const GOOGLETV_APK_PATH = 'googletv/signage-google-tv-latest.apk';
const GOOGLETV_MANIFEST_PATH = 'googletv/manifest.json';

const APK_CONTENT_TYPE = 'application/vnd.android.package-archive';

interface PlatformManifest {
  version: string | null;
  sha256: string | null;
  fileName: string;
  uploadedAt: string;
}

export interface UploadApkOptions {
  version?: string;
  sha256?: string;
}

// ---------------------------------------------------------------------------
// Shared helpers
// ---------------------------------------------------------------------------

function publicUrl(path: string, cacheBust?: string | null): string {
  const { data } = supabase.storage.from(BUCKET).getPublicUrl(path);
  if (!cacheBust) return data.publicUrl;
  const url = new URL(data.publicUrl);
  url.searchParams.set('v', cacheBust);
  return url.toString();
}

async function fetchManifest(manifestPath: string): Promise<PlatformManifest | null> {
  const { data, error } = await supabase.storage.from(BUCKET).download(manifestPath);
  if (!error && data) {
    return (await data.text().then((text) => JSON.parse(text))) as PlatformManifest;
  }

  try {
    const res = await fetch(publicUrl(manifestPath, Date.now().toString()), {
      cache: 'no-store',
    });
    if (!res.ok) return null;
    return (await res.json()) as PlatformManifest;
  } catch {
    return null;
  }
}

async function uploadApkAndManifest(
  file: File,
  apkPath: string,
  manifestPath: string,
  opts: UploadApkOptions,
): Promise<void> {
  const { error: apkError } = await supabase.storage.from(BUCKET).upload(apkPath, file, {
    upsert: true,
    contentType: APK_CONTENT_TYPE,
    cacheControl: '0',
  });
  if (apkError) throw new Error(apkError.message);

  const manifest: PlatformManifest = {
    version: opts.version?.trim() || null,
    sha256: opts.sha256?.trim() || null,
    fileName: file.name,
    uploadedAt: new Date().toISOString(),
  };
  const manifestBlob = new Blob([JSON.stringify(manifest)], {
    type: 'application/json',
  });
  const { error: manifestError } = await supabase.storage
    .from(BUCKET)
    .upload(manifestPath, manifestBlob, {
      upsert: true,
      contentType: 'application/json',
      cacheControl: '0',
    });
  if (manifestError) throw new Error(manifestError.message);
}

async function deleteApkAndManifest(apkPath: string, manifestPath: string): Promise<void> {
  const { error } = await supabase.storage.from(BUCKET).remove([apkPath, manifestPath]);
  if (error) throw new Error(error.message);
}

// ---------------------------------------------------------------------------
// Fire TV
// ---------------------------------------------------------------------------

/** Returns the current Fire TV APK asset from storage, or null if none uploaded. */
export async function getFireTvApkAsset(): Promise<FireTvApkDownload | null> {
  const manifest = await fetchManifest(FIRETV_MANIFEST_PATH);
  if (!manifest) return null;

  const repoSlug = resolveGithubRepoSlug();
  return {
    source: 'env-override',
    downloadUrl: publicUrl(FIRETV_APK_PATH, manifest.uploadedAt),
    fileName: manifest.fileName,
    version: manifest.version,
    sha256: manifest.sha256,
    releaseNotesUrl: null,
    sideloadGuideUrl: buildFireTvSideloadGuideUrl(repoSlug),
  };
}

/** Uploads a new Fire TV APK and writes the manifest. Replaces any existing file. */
export async function uploadFireTvApk(
  file: File,
  opts: UploadApkOptions = {},
): Promise<void> {
  await uploadApkAndManifest(file, FIRETV_APK_PATH, FIRETV_MANIFEST_PATH, opts);
}

/** Removes the Fire TV APK and its manifest from storage. */
export async function deleteFireTvApk(): Promise<void> {
  await deleteApkAndManifest(FIRETV_APK_PATH, FIRETV_MANIFEST_PATH);
}

// Legacy alias retained so existing callers that pass a typed options object
// keep type-checking. New code should prefer `UploadApkOptions`.
export type UploadFireTvApkOptions = UploadApkOptions;

// ---------------------------------------------------------------------------
// Google TV / Android TV
// ---------------------------------------------------------------------------

/** Returns the current Google TV APK asset from storage, or null if none uploaded. */
export async function getGoogleTvApkAsset(): Promise<GoogleTvApkDownload | null> {
  const manifest = await fetchManifest(GOOGLETV_MANIFEST_PATH);
  if (!manifest) return null;

  const repoSlug = resolveGithubRepoSlug();
  return {
    source: 'env-override',
    downloadUrl: publicUrl(GOOGLETV_APK_PATH, manifest.uploadedAt),
    fileName: manifest.fileName,
    version: manifest.version,
    sha256: manifest.sha256,
    releaseNotesUrl: null,
    sideloadGuideUrl: buildGoogleTvSideloadGuideUrl(repoSlug),
  };
}

/** Uploads a new Google TV APK and writes the manifest. Replaces any existing file. */
export async function uploadGoogleTvApk(
  file: File,
  opts: UploadApkOptions = {},
): Promise<void> {
  await uploadApkAndManifest(file, GOOGLETV_APK_PATH, GOOGLETV_MANIFEST_PATH, opts);
}

/** Removes the Google TV APK and its manifest from storage. */
export async function deleteGoogleTvApk(): Promise<void> {
  await deleteApkAndManifest(GOOGLETV_APK_PATH, GOOGLETV_MANIFEST_PATH);
}

export type UploadGoogleTvApkOptions = UploadApkOptions;
